import { gsap } from "gsap";
import { onReady } from "../core/ready.js";

let cleanupSticky = null;
let cleanupFixedChildMenus = null;
let cleanupNavbarMenuCollapseLinks = null;
let cleanupNavbarMobileSubmenus = null;
let cleanupNavbarOffcanvasState = null;

function initStickyMenu() {
    if (cleanupSticky) {
        cleanupSticky();
    }

    const header = document.querySelector('.page-header');
    if (!header) {
        cleanupSticky = null;
        return;
    }

    let isSticky = false;
    const isSinglePostPage = document.body.classList.contains('single-post');
    let threshold = isSinglePostPage ? -1 : window.innerHeight / 3;
    let ticking = false;

    function showHeader() {
        if (!isSticky) {
            gsap.killTweensOf(header);
            header.classList.add('sticky');
            void header.offsetHeight;
            gsap.set(header, { yPercent: -200 });

            requestAnimationFrame(() => {
                gsap.to(header, {
                    yPercent: 0,
                    duration: 0.3,
                    ease: "power3.out",
                    overwrite: true
                });
            });
            
            isSticky = true;
        }
    }

    function hideHeader() {
        if (isSticky) {
            gsap.killTweensOf(header);
            gsap.to(header, {
                yPercent: -200,
                duration: 0.3,
                ease: "power2.in",
                overwrite: true,
                onComplete: () => {
                    gsap.set(header, { clearProps: "transform" });
                    header.classList.remove('sticky');
                }
            });
            
            isSticky = false;
        }
    }

    function handleScroll() {
        const scrollY = window.scrollY;
        if (scrollY > threshold) {
            showHeader();
        } else {
            hideHeader();
        }
    }

    function onScroll() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
            handleScroll();
            ticking = false;
        });
    }

    function onResize() {
        threshold = isSinglePostPage ? -1 : window.innerHeight / 3;
        handleScroll();
    }

    window.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);
    handleScroll();

    cleanupSticky = () => {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
        gsap.killTweensOf(header);
        gsap.set(header, { clearProps: "transform" });
        header.classList.remove('sticky');
    };
}

onReady(initStickyMenu);

function initFixedChildMenus() {
    if (cleanupFixedChildMenus) {
        cleanupFixedChildMenus();
    }

    const menuItems = Array.from(
        document.querySelectorAll('.navbar__menu > .navbar__item.menu-item-has-children, .navbar__menu > .navbar__item.dropdown')
    );

    if (!menuItems.length) {
        cleanupFixedChildMenus = null;
        return;
    }

    const desktopMq = window.matchMedia('(min-width: 1200px)');

    const listeners = [];

    function getFixedContainingBlock(element) {
        let current = element.parentElement;

        while (current && current !== document.documentElement) {
            const styles = window.getComputedStyle(current);
            const hasContainingBlock =
                styles.transform !== 'none' ||
                styles.perspective !== 'none' ||
                styles.filter !== 'none' ||
                styles.backdropFilter !== 'none' ||
                styles.contain.includes('paint') ||
                styles.willChange.includes('transform') ||
                styles.willChange.includes('filter') ||
                styles.willChange.includes('perspective');

            if (hasContainingBlock) {
                return current;
            }

            current = current.parentElement;
        }

        return null;
    }

    function positionChildMenu(item) {
        const childMenu = item.querySelector(':scope > .child-menu');
        if (!childMenu) return;

        const anchor = item.querySelector(':scope > .navbar__link') || item;
        const rect = anchor.getBoundingClientRect();
        const fixedContainingBlock = getFixedContainingBlock(childMenu);

        let left = rect.left;
        let top = rect.bottom;

        if (fixedContainingBlock) {
            const containingRect = fixedContainingBlock.getBoundingClientRect();
            left -= containingRect.left;
            top -= containingRect.top;
        }

        childMenu.style.setProperty('--child-menu-left', `${left}px`);
        childMenu.style.setProperty('--child-menu-top', `${top}px`);
    }

    function positionAllChildMenus() {
        if (!desktopMq.matches) {
            menuItems.forEach((item) => {
                const childMenu = item.querySelector(':scope > .child-menu');
                if (!childMenu) return;
                childMenu.style.removeProperty('--child-menu-left');
                childMenu.style.removeProperty('--child-menu-top');
            });
            return;
        }

        menuItems.forEach(positionChildMenu);
    }

    menuItems.forEach((item) => {
        const onEnter = () => positionChildMenu(item);
        const onFocus = () => positionChildMenu(item);

        item.addEventListener('mouseenter', onEnter);
        item.addEventListener('focusin', onFocus);

        listeners.push(() => item.removeEventListener('mouseenter', onEnter));
        listeners.push(() => item.removeEventListener('focusin', onFocus));
    });

    window.addEventListener('resize', positionAllChildMenus);
    window.addEventListener('scroll', positionAllChildMenus, { passive: true });
    listeners.push(() => window.removeEventListener('resize', positionAllChildMenus));
    listeners.push(() => window.removeEventListener('scroll', positionAllChildMenus));

    positionAllChildMenus();

    cleanupFixedChildMenus = () => {
        listeners.forEach((removeListener) => removeListener());
        menuItems.forEach((item) => {
            const childMenu = item.querySelector(':scope > .child-menu');
            if (!childMenu) return;
            childMenu.style.removeProperty('--child-menu-left');
            childMenu.style.removeProperty('--child-menu-top');
        });
    };
}

onReady(initFixedChildMenus);

function initNavbarMenuCollapseLinks() {
    if (cleanupNavbarMenuCollapseLinks) {
        cleanupNavbarMenuCollapseLinks();
    }

    const menuContainer = document.getElementById('navbarMenuOffcanvas');
    if (!menuContainer) {
        cleanupNavbarMenuCollapseLinks = null;
        return;
    }

    const closeOffcanvas = () => {
        const bootstrapOffcanvas = window.bootstrap?.Offcanvas?.getOrCreateInstance(menuContainer);
        bootstrapOffcanvas?.hide();
    };

    const onMenuLinkClick = (event) => {
        const link = event.target.closest('a.nav-link, a.navbar__link, a.dropdown-link');
        if (!link || !menuContainer.contains(link)) {
            return;
        }

        const href = (link.getAttribute('href') || '').trim();
        const isInvalidHref = !href || href === '#' || href.toLowerCase().startsWith('javascript:');

        if (isInvalidHref) {
            event.preventDefault();
            return;
        }

        if (!link.classList.contains('dropdown-link')) {
            if (window.matchMedia('(max-width: 1199.98px)').matches) {
                closeOffcanvas();
            }
            return;
        }

        const isModifiedClick = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0;
        const target = (link.getAttribute('target') || '').toLowerCase();

        if (isModifiedClick || target === '_blank') {
            return;
        }

        // Ensure parent items with dropdown stay navigable when they have a real URL.
        event.preventDefault();
        window.location.assign(link.href);
        if (window.matchMedia('(max-width: 1199.98px)').matches) {
            closeOffcanvas();
        }
    };

    menuContainer.addEventListener('click', onMenuLinkClick, true);

    cleanupNavbarMenuCollapseLinks = () => {
        menuContainer.removeEventListener('click', onMenuLinkClick, true);
    };
}

onReady(initNavbarMenuCollapseLinks);

function initNavbarOffcanvasState() {
    if (cleanupNavbarOffcanvasState) {
        cleanupNavbarOffcanvasState();
    }

    const offcanvas = document.getElementById('navbarMenuOffcanvas');
    const toggleButton = document.getElementById('navbarMenuToggle');
    let scrollY = 0;
    if (!offcanvas) {
        cleanupNavbarOffcanvasState = null;
        return;
    }

    const onShow = () => {
        scrollY = window.scrollY || window.pageYOffset || 0;
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
        document.documentElement.classList.add('offcanvas-open');
        document.body.classList.add('offcanvas-open');
        toggleButton?.classList.add('navbar-collapse-show');
    };

    const onHide = () => {
        document.documentElement.style.removeProperty('overflow');
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('position');
        document.body.style.removeProperty('top');
        document.body.style.removeProperty('left');
        document.body.style.removeProperty('right');
        document.body.style.removeProperty('width');
        document.documentElement.classList.remove('offcanvas-open');
        document.body.classList.remove('offcanvas-open');
        toggleButton?.classList.remove('navbar-collapse-show');
        window.scrollTo(0, scrollY);
    };

    offcanvas.addEventListener('show.bs.offcanvas', onShow);
    offcanvas.addEventListener('hidden.bs.offcanvas', onHide);

    cleanupNavbarOffcanvasState = () => {
        offcanvas.removeEventListener('show.bs.offcanvas', onShow);
        offcanvas.removeEventListener('hidden.bs.offcanvas', onHide);
        document.documentElement.style.removeProperty('overflow');
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('position');
        document.body.style.removeProperty('top');
        document.body.style.removeProperty('left');
        document.body.style.removeProperty('right');
        document.body.style.removeProperty('width');
        document.documentElement.classList.remove('offcanvas-open');
        document.body.classList.remove('offcanvas-open');
        toggleButton?.classList.remove('navbar-collapse-show');
    };
}

onReady(initNavbarOffcanvasState);

function initNavbarMobileSubmenus() {
    if (cleanupNavbarMobileSubmenus) {
        cleanupNavbarMobileSubmenus();
    }

    const offcanvas = document.getElementById('navbarMenuOffcanvas');
    const menuItems = offcanvas
        ? Array.from(
            offcanvas.querySelectorAll('.navbar__menu > .navbar__item.menu-item-has-children, .navbar__menu > .navbar__item.dropdown')
        )
        : [];
    if (!menuItems.length) {
        cleanupNavbarMobileSubmenus = null;
        return;
    }

    const listeners = [];
    const desktopMq = window.matchMedia('(min-width: 1200px)');

    menuItems.forEach((item, index) => {
        const childMenu = item.querySelector(':scope > .child-menu');
        const link = item.querySelector(':scope > .navbar__link');
        if (!childMenu || !link) return;

        const submenuId = childMenu.id || `navbar-submenu-${index}`;
        childMenu.id = submenuId;

        let toggleButton = item.querySelector(':scope > .navbar__submenu-toggle');
        if (!toggleButton) {
            toggleButton = document.createElement('button');
            toggleButton.type = 'button';
            toggleButton.className = 'navbar__submenu-toggle';
            toggleButton.setAttribute('aria-label', 'Apri sottomenu');
            toggleButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" aria-hidden="true" focusable="false"><path d="M441.9 167.3l-19.8-19.8c-4.7-4.7-12.3-4.7-17 0L224 328.2 42.9 147.5c-4.7-4.7-12.3-4.7-17 0L6.1 167.3c-4.7 4.7-4.7 12.3 0 17l209.4 209.4c4.7 4.7 12.3 4.7 17 0l209.4-209.4c4.7-4.7 4.7-12.3 0-17z"/></svg>';
            item.insertBefore(toggleButton, childMenu);
        }

        toggleButton.setAttribute('aria-controls', submenuId);
        toggleButton.setAttribute('aria-expanded', 'false');
        item.classList.remove('is-submenu-open');

        const onToggleClick = (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (desktopMq.matches) return;

            menuItems.forEach((otherItem) => {
                if (otherItem === item) return;
                otherItem.classList.remove('is-submenu-open');
                const otherToggle = otherItem.querySelector(':scope > .navbar__submenu-toggle');
                if (otherToggle) {
                    otherToggle.setAttribute('aria-expanded', 'false');
                }
            });

            const isOpen = item.classList.toggle('is-submenu-open');
            toggleButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        };

        toggleButton.addEventListener('click', onToggleClick);
        listeners.push(() => toggleButton.removeEventListener('click', onToggleClick));
    });

    const onResize = () => {
        if (!desktopMq.matches) return;
        menuItems.forEach((item) => {
            const toggleButton = item.querySelector(':scope > .navbar__submenu-toggle');
            item.classList.remove('is-submenu-open');
            if (toggleButton) {
                toggleButton.setAttribute('aria-expanded', 'false');
            }
        });
    };

    window.addEventListener('resize', onResize);
    listeners.push(() => window.removeEventListener('resize', onResize));

    cleanupNavbarMobileSubmenus = () => {
        listeners.forEach((removeListener) => removeListener());
    };
}

onReady(initNavbarMobileSubmenus);
